import React, { useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import CircularProgress from "@mui/material/CircularProgress";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import SearchIcon from "@mui/icons-material/Search";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";

import { getTakeawayOrders } from "../../../../../services/Admin/SalesScreenApi";
import OpenOrderDialog from "../OrderActions/OpenOrder/OpenOrderDialog";
import OrderInfo from "../OrderDetailsInfo";
import DeliveryTakeAwayOrdersCard from "./OrderCard/DeliveryTakeAwayOrdersCard";

const TakeAwayOrders = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [openOrder, setOpenOrder] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [filterUser, setFilterUser] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterType, setFilterType] = useState("");
  const [underReviewOnly, setUnderReviewOnly] = useState(false);

  const fetch = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getTakeawayOrders();
      setRows(res?.data || []);
    } catch (err) {
      setError(err?.message || "Failed to load takeaway orders");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetch(); }, []);

  // الحصول على قوائم فريدة للفلاتر
  const uniqueUsers = [...new Set(rows.map(order => order.user?.name).filter(Boolean))];
  const uniqueStatuses = [...new Set(rows.map(order => order.status).filter(Boolean))];
  const uniqueTypes = [...new Set(rows.map(order => order.type).filter(Boolean))];
  const underReviewCount = useMemo(
    () => rows.filter((order) => order?.status === "under_review").length,
    [rows]
  );

  // فلترة الطلبات بناءً على بحث العميل والفلاتر
  const filteredRows = rows.filter((order) => {
    if (underReviewOnly && order?.status !== "under_review") {
      return false;
    }

    // فلتر البحث النصي
    let matchesSearch = true;
    if (searchText) {
      const searchLower = searchText.toLowerCase();
      const customer = order.customer;
      
      matchesSearch = (
        customer?.name?.toLowerCase().includes(searchLower) ||
        customer?.phone?.toLowerCase().includes(searchLower) ||
        customer?.phone2?.toLowerCase().includes(searchLower) ||
        customer?.phone3?.toLowerCase().includes(searchLower) ||
        customer?.email?.toLowerCase().includes(searchLower) ||
        customer?.address?.toLowerCase().includes(searchLower) ||
        customer?.address2?.toLowerCase().includes(searchLower) ||
        customer?.address3?.toLowerCase().includes(searchLower) ||
        order.general_increment?.toString().includes(searchText) ||
        order.daily_increment?.toString().includes(searchText) ||
        order.monthly_increment?.toString().includes(searchText)
      );
    }

    // فلتر المستخدم
    const matchesUser = !filterUser || order.user?.name === filterUser;
    
    // فلتر الحالة
    const matchesStatus = !filterStatus || order.status === filterStatus;

    // فلتر نوع الطلب من المصدر (client/local/screen)
    const matchesType = !filterType || order.type === filterType;

    return matchesSearch && matchesUser && matchesStatus && matchesType;
  });

  const handleUnderReviewAlertClick = () => {
    if (underReviewOnly) {
      setUnderReviewOnly(false);
      setFilterStatus("");
      return;
    }

    setUnderReviewOnly(true);
    setFilterStatus("under_review");
    setSearchText("");
    setFilterUser("");
    setFilterType("");
  };

  const isInitialLoading = loading && rows.length === 0;

  if (isInitialLoading) return (
    <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
      <CircularProgress />
    </Box>
  );

  if (error) return <Box sx={{ color: "error.main", p: 2 }}>{String(error)}</Box>;

  return (
    <Box>
      {underReviewCount > 0 && (
        <Alert
          severity="warning"
          variant="filled"
          onClick={handleUnderReviewAlertClick}
          sx={{
            mb: 2,
            cursor: "pointer",
            userSelect: "none",
            "& .MuiAlert-message": { width: "100%" },
          }}
        >
          <AlertTitle>يوجد {underReviewCount} طلب تيك أواي بانتظار المراجعة</AlertTitle>
          {underReviewOnly
            ? "جاري عرض طلبات (طلب خارجي) فقط. اضغط لإلغاء الفلتر."
            : "اضغط هنا لعرض كل طلبات التيك أواي التي حالتها (طلب خارجي)."}
        </Alert>
      )}

      {loading && rows.length > 0 && (
        <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
          <CircularProgress size={22} />
        </Box>
      )}

      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2, gap: 2, flexWrap: "wrap" }}>
        <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", flexGrow: 1 }}>
          <TextField
            placeholder="ابحث بالاسم، رقم الهاتف، العنوان، رقم الطلب..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            size="small"
            sx={{ flexGrow: 1, minWidth: 250 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>المستخدم</InputLabel>
            <Select
              value={filterUser}
              onChange={(e) => setFilterUser(e.target.value)}
              label="المستخدم"
            >
              <MenuItem value="">الكل</MenuItem>
              {uniqueUsers.map((user) => (
                <MenuItem key={user} value={user}>{user}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>الحالة</InputLabel>
            <Select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              label="الحالة"
            >
              <MenuItem value="">الكل</MenuItem>
              {uniqueStatuses.map((status) => (
                <MenuItem key={status} value={status}>
                  {status === "pending" ? "قيد التجهيز" :
                   status === "in_prepration" || status === "in_preparation" ? "في التحضير" :
                   status === "pickup" ? "تم الاستلام من العميل" :
                   status === "out_of_preparation" ? "خارج التحضير" :
                   status === "under_review" ? "تحت المراجعة" :
                   status === "finished" ? "منتهي" :
                   status === "cancelled" ? "ملغي" : status}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>نوع الطلب</InputLabel>
            <Select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              label="نوع الطلب"
            >
              <MenuItem value="">الكل</MenuItem>
              {uniqueTypes.map((type) => (
                <MenuItem key={type} value={type}>
                  {type === "client"
                    ? "تم من خلال العميل"
                    : type === "local"
                    ? "تم من خلال كاشير"
                    : type === "screen"
                    ? "تم من خلال شاشة"
                    : type}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        
        <Button variant="contained" onClick={() => setOpenCreateDialog(true)}>
          إنشاء طلب
        </Button>
      </Box>

      <Grid container spacing={1.5}>
        {filteredRows.map((r) => (
          <Grid item xs={12} sm={6} md={4} lg={3} xl={2.4} key={r.id}>
            <Box onClick={() => setOpenOrder(r)} sx={{ cursor: 'pointer', height: '100%' }}>
              <DeliveryTakeAwayOrdersCard
                order={r}
                orderType="takeaway"
                onOrderUpdated={fetch}
              />
            </Box>
          </Grid>
        ))}
      </Grid>
      
      {filteredRows.length === 0 && !loading && (
        <Box sx={{ textAlign: "center", py: 4, color: "text.secondary" }}>
          {searchText || filterUser || filterStatus || filterType
            ? "لا توجد نتائج للبحث"
            : "لا توجد طلبات"}
        </Box>
      )}

      <Dialog open={!!openOrder} onClose={() => setOpenOrder(null)} maxWidth="lg" fullWidth>
        <OrderInfo 
          order={openOrder} 
          onRefresh={fetch} 
          onInvoicePaymentSuccess={() => setOpenOrder(null)} 
        />
      </Dialog>

      <OpenOrderDialog
        open={openCreateDialog}
        onClose={() => setOpenCreateDialog(false)}
        onCreated={fetch}
        initialOrderType="takeaway"
      />
    </Box>
  );
};

export default TakeAwayOrders;
